import { GridApi } from 'ag-grid-community';
import { useCallback, useRef } from 'react';

/**
 * Hook for lazy loading and updating AG Grid cell values
 */
export const useLazyGridValue = <T = any>() => {
  const cache = useRef<Record<string, T>>({});
  const pendingFetches = useRef(new Set<string>());
  const gridApiRef = useRef<GridApi | null>(null);

  /**
   * Set the grid API reference
   */
  const setGridApi = useCallback((api: GridApi) => {
    gridApiRef.current = api;
  }, []);

  /**
   * Lazy load a value and update the grid when ready
   * @param key - Unique identifier (e.g., deviceId)
   * @param fetchFn - Async function to fetch the value
   * @param field - Grid column field to update
   * @returns Current cached value or undefined
   */
  const lazyLoadValue = useCallback(
    async (
      key: string,
      fetchFn: () => Promise<T>,
      field: string,
    ): Promise<void> => {
      // Prevent duplicate fetches
      if (pendingFetches.current.has(key)) return;

      pendingFetches.current.add(key);

      try {
        const value = await fetchFn();

        // Cache the value
        cache.current[key] = value;

        // Update the grid
        if (gridApiRef.current) {
          const rowNode = gridApiRef.current.getRowNode(key);
          if (rowNode) {
            rowNode.setDataValue(field, value);
          }
        }
      } catch (error) {
        console.error(`Failed to fetch value for key:`, key, error);
        cache.current[key] = null as T;
      } finally {
        pendingFetches.current.delete(key);
      }
    },
    [],
  );

  /**
   * Get cached value or trigger lazy load
   * Use this in your valueGetter
   */
  const getValue = useCallback(
    (key: string, fetchFn: () => Promise<T>, field: string): T | null => {
      const cached = cache.current[key];

      // Return cached value if available
      if (cached !== undefined) {
        return cached;
      }

      // Trigger lazy load (don't await)
      void lazyLoadValue(key, fetchFn, field);

      // Return null while loading
      return null;
    },
    [lazyLoadValue],
  );

  return {
    setGridApi,
    getValue,
  };
};
